import Button from '@creator/ui/components/Button/ButtonV2';
import Text from '@creator/ui/components/Text/Text';
import { cn } from '@creator/ui/utils/ui';
import EmptyState from '@src/components/Post/EmptyState';
import PostCardCompact from '@src/components/Post/PostCard/PostCardCompact';
import { ensureTokenUrl } from '@src/model/config/helpers';
import { usePaginatedPosts } from '@src/store/posts/hooks/usePaginatedPosts';
import type { FC } from 'react';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

interface CommunityPostsProps {
    tokenName: string;
    flairId?: string;
    className?: string;
}

const CommunityPosts: FC<CommunityPostsProps> = props => {
    const { tokenName, flairId, className = '' } = props;
    const { postIds, loadMore, loadStatus, hasMore } = usePaginatedPosts({ type: 'community', tokenName, flairId, fetchLimit: 12, includeMyReactions: true });
    const { postIds: pinnedPostsIds, loadMore: loadPinnedPosts } = usePaginatedPosts({ type: 'pinnedCommunity', tokenName, flairId, includeMyReactions: true });
    const { t } = useTranslation();
    const navigate = useNavigate();

    // --- Combine Posts ---
    const combinedPosts = useMemo(() => {
        return Array.from(new Set([...pinnedPostsIds, ...postIds]));
    }, [postIds, pinnedPostsIds]);

    useEffect(() => {
        if (pinnedPostsIds.length > 0) return;
        loadPinnedPosts();
    }, [tokenName, flairId]);

    useEffect(() => {
        if (postIds.length > 0) return; // skip load if there are items already
        loadMore();
    }, [flairId]);

    const onBackToTopClick = useCallback(() => {
        // Find the visible partner-posts element (avoid selecting hidden Activity elements)
        const elements = document.querySelectorAll('#partner-posts');
        const visibleElement = Array.from(elements).find(el => {
            const rect = el.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
        });
        if (visibleElement) return visibleElement.scrollIntoView({ behavior: 'smooth' });
        navigate(`/${ensureTokenUrl(tokenName)}`);
    }, []);

    function renderLoadMoreButton() {
        if (!hasMore) return;

        return (
            <Button id="load-more-community-posts" color="base" isLoading={loadStatus === 'loading'} size="md" className="flex-1 w-full" onClick={loadMore}>
                <Text>{t('postList.community.loadMoreButton')}</Text>
            </Button>
        );
    }

    function renderBackToTopButton() {
        if (!hasMore) return;

        return (
            <Button size="md" className="flex-1 w-full" onClick={onBackToTopClick}>
                <Text>{t('postList.community.backToTopButton')}</Text>
            </Button>
        );
    }

    function renderPost(postId: string) {
        return <PostCardCompact key={postId} tokenName={tokenName} className={cn('bg-transparent dark:bg-transparent')} postId={postId} />;
    }

    function renderPosts() {
        return <div className="grid grid-cols-1 gap-3 gap-x-4 md:grid-cols-2">{combinedPosts.map(renderPost)}</div>;
    }

    function renderContent() {
        if (loadStatus === 'success' && combinedPosts.length === 0) return <EmptyState context="post" type="community" />;
        return (
            <div id="community-posts" className={cn('space-y-4', className)}>
                {renderPosts()}
                <div className="flex max-w-3xl mx-auto space-x-4">
                    {renderLoadMoreButton()}
                    {renderBackToTopButton()}
                </div>
            </div>
        );
    }

    return renderContent();
};

export default memo(CommunityPosts);
